/**
 * Leviathan Web - Tax Report Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Tax liability by jurisdiction and type
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  RefreshCw,
  Printer,
  DollarSign,
  FileText,
  Fuel,
  Calculator,
  Receipt,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface TaxReport {
  dateRange: { startDate: string; endDate: string };
  generatedAt: string;
  summary: {
    transactionCount: number;
    totalTaxable: number;
    totalTax: number;
    totalGallons: number;
    exemptAmount: number;
  };
  byTaxCode: Array<{
    taxRateCode: string;
    taxRateName: string;
    rateType: string;
    transactionCount: number;
    totalTaxable: number;
    totalTax: number;
    totalGallons: number;
    exemptCount: number;
    exemptAmount: number;
  }>;
}

const RATE_TYPE_LABELS: Record<string, string> = {
  PERCENT: '%',
  PER_GALLON: '/gal',
  FLAT: 'flat',
};

/**
 * Stat card component
 */
function StatCard({
  label,
  value,
  icon,
  highlight = false,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <div
      className={`relative bg-panel-dark border p-4 ${highlight ? 'border-amber' : 'border-panel-border'}`}
    >
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="flex items-center space-x-2 mb-2">
        <span className={highlight ? 'text-amber' : 'text-text-tertiary'}>{icon}</span>
        <span className="text-text-tertiary text-xs uppercase tracking-widest font-mono">
          {label}
        </span>
      </div>
      <div
        className={`text-xl font-mono font-bold ${highlight ? 'text-amber' : 'text-text-primary'}`}
        style={highlight ? { textShadow: '0 0 10px rgba(255,149,0,0.6)' } : undefined}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * Tax Report Page
 */
const TaxReport = () => {
  const { accessToken } = useAuth();
  const [report, setReport] = useState<TaxReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Default to current month
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(firstOfMonth.toISOString().split('T')[0] || '');
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0] || '');

  useEffect(() => {
    fetchReport();
  }, [startDate, endDate]);

  const fetchReport = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/reports/tax?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setReport(data.report);
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const setQuickRange = (range: string) => {
    const now = new Date();
    let start: Date;
    let end: Date;

    switch (range) {
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
        break;
      case 'last-month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'quarter':
        start = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        end = now;
        break;
      case 'last-quarter': {
        const lastQStart = Math.floor(now.getMonth() / 3) * 3 - 3;
        start = new Date(now.getFullYear(), lastQStart, 1);
        end = new Date(now.getFullYear(), lastQStart + 3, 0);
        break;
      }
      case 'year':
        start = new Date(now.getFullYear(), 0, 1);
        end = now;
        break;
      default:
        return;
    }

    setStartDate(start.toISOString().split('T')[0] || '');
    setEndDate(end.toISOString().split('T')[0] || '');
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/reports"
            className="p-2 text-text-tertiary hover:text-amber transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-text-primary font-mono uppercase tracking-wider">
              Tax Report
            </h1>
            <p className="text-sm text-text-secondary font-mono mt-1">
              Tax liability by jurisdiction and type
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => window.print()}
            className="flex items-center space-x-2 px-4 py-2 border border-panel-border text-text-secondary hover:text-amber hover:border-amber transition-colors"
          >
            <Printer className="w-4 h-4" />
            <span className="font-mono text-sm">Print</span>
          </button>
        </div>
      </div>

      {/* Date Controls */}
      <div className="bg-panel-dark border border-panel-border p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-background border border-panel-border focus:border-amber px-4 py-2 text-text-primary font-mono focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-background border border-panel-border focus:border-amber px-4 py-2 text-text-primary font-mono focus:outline-none"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'month', label: 'This Month' },
              { key: 'last-month', label: 'Last Month' },
              { key: 'quarter', label: 'This Quarter' },
              { key: 'last-quarter', label: 'Last Quarter' },
              { key: 'year', label: 'YTD' },
            ].map((range) => (
              <button
                key={range.key}
                onClick={() => setQuickRange(range.key)}
                className="bg-background border border-panel-border hover:border-amber/50 px-3 py-2 text-text-secondary hover:text-amber font-mono text-xs uppercase transition-colors"
              >
                {range.label}
              </button>
            ))}
          </div>
          <button
            onClick={fetchReport}
            disabled={isLoading}
            className="flex items-center space-x-2 bg-amber text-background px-4 py-2 font-mono text-sm uppercase tracking-wider hover:bg-amber/80 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard
            label="Total Tax Due"
            value={formatCurrency(report.summary.totalTax)}
            icon={<Calculator className="w-5 h-5" />}
            highlight
          />
          <StatCard
            label="Taxable Amount"
            value={formatCurrency(report.summary.totalTaxable)}
            icon={<DollarSign className="w-5 h-5" />}
          />
          <StatCard
            label="Total Gallons"
            value={report.summary.totalGallons.toLocaleString()}
            icon={<Fuel className="w-5 h-5" />}
          />
          <StatCard
            label="Transactions"
            value={report.summary.transactionCount.toString()}
            icon={<FileText className="w-5 h-5" />}
          />
          <StatCard
            label="Exempt Amount"
            value={formatCurrency(report.summary.exemptAmount)}
            icon={<Receipt className="w-5 h-5" />}
          />
        </div>
      )}

      {/* Tax Breakdown Table */}
      <div className="relative">
        <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border overflow-x-auto">
          {isLoading ? (
            <div className="text-center py-12 text-text-tertiary font-mono">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading report...
            </div>
          ) : !report || report.byTaxCode.length === 0 ? (
            <div className="text-center py-12">
              <Calculator className="w-12 h-12 text-text-tertiary mx-auto mb-3" />
              <div className="text-text-tertiary font-mono">No tax transactions in this period</div>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-background border-b border-panel-border">
                <tr>
                  <th className="text-left px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Tax
                  </th>
                  <th className="text-center px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Type
                  </th>
                  <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Transactions
                  </th>
                  <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Gallons
                  </th>
                  <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Taxable
                  </th>
                  <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Exempt
                  </th>
                  <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Tax Due
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.byTaxCode.map((row) => (
                  <tr
                    key={row.taxRateCode}
                    className="border-b border-panel-border/50 hover:bg-amber/5"
                  >
                    <td className="px-4 py-3">
                      <div className="text-amber font-mono font-semibold">{row.taxRateCode}</div>
                      <div className="text-text-secondary text-sm">{row.taxRateName}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 text-xs font-mono bg-steel/20 text-steel border border-steel">
                        {RATE_TYPE_LABELS[row.rateType] || row.rateType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-text-secondary font-mono">
                      {row.transactionCount}
                    </td>
                    <td className="px-4 py-3 text-right text-text-secondary font-mono">
                      {row.totalGallons.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-text-secondary font-mono">
                      {formatCurrency(row.totalTaxable)}
                    </td>
                    <td className="px-4 py-3 text-right text-text-tertiary font-mono">
                      {row.exemptAmount > 0 ? (
                        <span title={`${row.exemptCount} exempt transactions`}>
                          {formatCurrency(row.exemptAmount)}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-amber font-mono font-bold">
                      {formatCurrency(row.totalTax)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-background border-t-2 border-amber font-bold">
                <tr>
                  <td colSpan={4} className="px-4 py-3 text-right text-text-primary font-mono uppercase">
                    Totals
                  </td>
                  <td className="px-4 py-3 text-right text-text-primary font-mono">
                    {formatCurrency(report.summary.totalTaxable)}
                  </td>
                  <td className="px-4 py-3 text-right text-text-tertiary font-mono">
                    {formatCurrency(report.summary.exemptAmount)}
                  </td>
                  <td className="px-4 py-3 text-right text-amber font-mono text-lg">
                    {formatCurrency(report.summary.totalTax)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      {/* Report Footer */}
      {report && (
        <div className="text-center text-text-tertiary font-mono text-xs">
          Report generated: {new Date(report.generatedAt).toLocaleString()} | Period:{' '}
          {new Date(report.dateRange.startDate).toLocaleDateString()} -{' '}
          {new Date(report.dateRange.endDate).toLocaleDateString()}
        </div>
      )}
    </div>
  );
};

export default TaxReport;
